import type { RawProductQuery } from '@packages/gql/generated/shopping/graphql';
import {
  convertProductDownloadsData,
  convertProductEnergyEfficiencyData,
  convertProductTireEfficiencyData,
} from '../helper/converter';
import { EnergyEfficiencyFlagsCard } from '../components/EnergyEfficiencyFlagsCard/EnergyEfficiencyFlagsCard';
import { TireEfficiencyFlagsCard } from '../components/TireEfficiencyFlagsCard/TireEfficiencyFlagsCard';

const hasEnergyEfficiencyFlags = (
  energyEfficiencyFlags: EfficiencyFlagsCardsProps['energyEfficiencyFlags'],
): energyEfficiencyFlags is NonNullable<EfficiencyFlagsCardsProps['energyEfficiencyFlags']> =>
  !!energyEfficiencyFlags && energyEfficiencyFlags.length > 0;

const hasTireEfficiencyFlags = (
  tireEfficiencyFlags: EfficiencyFlagsCardsProps['tireEfficiencyFlags'],
): tireEfficiencyFlags is NonNullable<EfficiencyFlagsCardsProps['tireEfficiencyFlags']> =>
  !!tireEfficiencyFlags && tireEfficiencyFlags.flags.length > 0;

/**
 * Returns whether either energy or tire efficiency flags are available.
 *
 * Use this helper to avoid rendering empty markup when no efficiency data exists.
 */
export const checkRenderEfficiencyFlagsCards = ({
  energyEfficiencyFlags,
  tireEfficiencyFlags,
}: Pick<EfficiencyFlagsCardsProps, 'energyEfficiencyFlags' | 'tireEfficiencyFlags'>) =>
  hasEnergyEfficiencyFlags(energyEfficiencyFlags) || hasTireEfficiencyFlags(tireEfficiencyFlags);

/**
 * Props for rendering product efficiency cards in product context.
 */
export type EfficiencyFlagsCardsProps = {
  energyEfficiencyFlags: NonNullable<RawProductQuery['product']>['powerEfficiencyFlags'];
  energyEfficiencyFlagsUnmatchedDataSheets?: string[];
  tireEfficiencyFlags: NonNullable<RawProductQuery['product']>['tireEfficiency'];
  downloads?: NonNullable<RawProductQuery['product']>['downloads'];
};

/**
 * Renders efficiency cards for Product specific data.
 *
 * Use `checkRenderEfficiencyFlagsCards` to check if any efficiency data is available before rendering this component.
 *
 * @example
 * ```tsx
 * checkRenderEfficiencyFlagsCards({ energyEfficiencyFlags, tireEfficiencyFlags }) && (
 *   <EfficiencyFlagsCards
 *     energyEfficiencyFlags={energyEfficiencyFlags}
 *     energyEfficiencyFlagsUnmatchedDataSheets={energyEfficiencyFlagsUnmatchedDataSheets}
 *     tireEfficiencyFlags={tireEfficiencyFlags}
 *     downloads={downloads}
 *   />
 * )
 * ```
 */
export const EfficiencyFlagsCards = ({
  energyEfficiencyFlags,
  energyEfficiencyFlagsUnmatchedDataSheets,
  tireEfficiencyFlags,
  downloads,
}: EfficiencyFlagsCardsProps) => {
  if (hasEnergyEfficiencyFlags(energyEfficiencyFlags)) {
    const convertedEnergyEfficiencyFlags =
      convertProductEnergyEfficiencyData(energyEfficiencyFlags);

    return (
      <EnergyEfficiencyFlagsCard
        efficiencyFlags={convertedEnergyEfficiencyFlags}
        unmatchedDataSheets={energyEfficiencyFlagsUnmatchedDataSheets}
      />
    );
  }

  if (!hasTireEfficiencyFlags(tireEfficiencyFlags)) {
    return null;
  }

  const convertedTireEfficiencyFlags = convertProductTireEfficiencyData(tireEfficiencyFlags);
  const filteredDownloads = downloads ? convertProductDownloadsData(downloads) : undefined;

  return (
    <TireEfficiencyFlagsCard
      efficiencyFlags={convertedTireEfficiencyFlags}
      dataSheets={filteredDownloads}
    />
  );
};
